import { useEffect, useState, useSyncExternalStore } from 'react'
import type { LatLng } from '@/features/detection/domain/geometry'
import type { TrackerState } from '../application/tracking/PositionTracker'
import { filterSpeed, initialSpeed, type SpeedState } from '../domain/map/fieldMeasures'
import { initialCamera, stepCamera, type CameraOutput, type CameraState } from '../domain/map/mapCamera'
import type { RoutePolyline } from '../domain/map/pathGeometry'
import type { GuideAvailability } from '../domain/map/routeGuide'
import { initialProgress, stepProgress, type ProgressState } from '../domain/map/routeProgress'

// Représentation visuelle de la carte terrain : progression le long du parcours prévu, symbole et orientation
// stabilisés. Lit la position réelle du suivi sans jamais la modifier ni la renvoyer : détection, télémétrie et journal
// continuent de recevoir les vrais relevés.

/** Au-delà, la position n'est plus « fraîche » pour la carte (dernière représentation, incertitude). */
export const MAP_FIX_MAX_AGE_MS = 15_000
/** Précision au-delà de laquelle le symbole n'avance plus. */
export const MAP_FIX_MAX_ACCURACY_M = 35

export interface StableMap {
  camera: CameraOutput
  /** Position projetée le long du parcours (m); `null` : hors parcours, sans parcours ou incertaine. */
  along: number | null
  /** Portion parcourue avec certitude (m), pour l'atténuation du tracé. */
  travelled: number
  direction: ProgressState['direction']
  speed: SpeedState
}

interface StableInput {
  operator: TrackerState
  polyline: RoutePolyline | null
  work: { itemId: string; entry: LatLng } | null
  paused: boolean
}

const initialOutput: StableMap = {
  camera: { mode: 'TRAVEL', symbol: null, heading: 0, stabilized: false, uncertain: true, headingSource: 'FROZEN' },
  along: null,
  travelled: 0,
  direction: null,
  speed: initialSpeed,
}

/** État hors React : un pas par nouveau relevé ou changement métier, quelques images pendant une animation. */
export class StableMapStore {
  private progress: ProgressState = initialProgress
  private polyline: RoutePolyline | null = null
  private camera: CameraState = initialCamera
  private speed: SpeedState = initialSpeed
  private lastFixAt: number | null = null
  private along: number | null = null
  private output: StableMap = initialOutput
  private input: StableInput | null = null
  private frame: number | null = null
  private readonly listeners = new Set<() => void>()

  constructor(private readonly now: () => number = () => Date.now()) {}

  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  snapshot = () => this.output

  update(input: StableInput) {
    this.input = input
    const { operator, polyline } = input
    if (polyline !== this.polyline) {
      this.polyline = polyline
      this.progress = initialProgress
      this.along = null
    }
    const position = operator.position
    const fresh = operator.lastFixAt !== null && operator.lastFixAt !== this.lastFixAt
    if (fresh && position && operator.lastFixAt !== null) {
      this.lastFixAt = operator.lastFixAt
      this.speed = filterSpeed(this.speed, operator.speed ?? null, operator.lastFixAt)
      if (polyline) {
        const stepped = stepProgress(polyline, this.progress, {
          lat: position.lat,
          lng: position.lng,
          accuracyM: operator.accuracy ?? null,
          at: operator.lastFixAt,
        })
        this.progress = stepped.state
        this.along = stepped.snapped?.along ?? null
        this.snapped = stepped.snapped
      }
    }
    this.step()
  }

  private snapped: { point: LatLng; bearing: number } | null = null

  private step() {
    const input = this.input
    if (!input) return
    const now = this.now()
    const { operator } = input
    const accuracy = operator.accuracy ?? null
    const gpsFresh =
      operator.position !== null &&
      operator.lastFixAt !== null &&
      (operator.status === 'ok' || operator.status === 'stale') &&
      now - operator.lastFixAt <= MAP_FIX_MAX_AGE_MS &&
      (accuracy === null || accuracy <= MAP_FIX_MAX_ACCURACY_M)
    const stepped = stepCamera(this.camera, {
      now,
      fix: operator.position ? { ...operator.position, accuracyM: accuracy } : null,
      gpsFresh,
      progress: input.polyline && this.along !== null ? this.snapped : null,
      work: input.work,
      paused: input.paused,
    })
    this.camera = stepped.state
    this.output = {
      camera: stepped.output,
      along: this.along,
      travelled: this.progress.travelled,
      direction: this.progress.direction,
      speed: this.speed,
    }
    for (const listener of this.listeners) listener()
    if (stepped.state.animation && this.frame === null && typeof requestAnimationFrame !== 'undefined') {
      this.frame = requestAnimationFrame(() => {
        this.frame = null
        this.step()
      })
    }
  }

  dispose() {
    if (this.frame !== null && typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(this.frame)
    this.frame = null
    this.listeners.clear()
  }
}

export function useStableMap({
  operator,
  guide,
  work,
  paused,
}: {
  operator: TrackerState
  guide: GuideAvailability
  work: { itemId: string; entry: LatLng } | null
  paused: boolean
}): StableMap {
  const [store] = useState(() => new StableMapStore())
  const polyline = guide.kind === 'READY' ? guide.polyline : null
  const workId = work?.itemId ?? null
  const workLat = work?.entry.lat ?? null
  const workLng = work?.entry.lng ?? null
  useEffect(() => {
    store.update({
      operator,
      polyline,
      work: workId !== null && workLat !== null && workLng !== null ? { itemId: workId, entry: { lat: workLat, lng: workLng } } : null,
      paused,
    })
  }, [store, operator, polyline, workId, workLat, workLng, paused])
  useEffect(
    () => () => {
      store.dispose()
    },
    [store],
  )
  return useSyncExternalStore(store.subscribe, store.snapshot)
}
